import { API_BASE, messageFromErrorBody } from './authApi';
import { authHeaders } from './authStorage';

const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  ...authHeaders(),
});

/** @typedef {Record<string, unknown>} RawPlanDetail */

export function mapDetailToForm(d) {
  const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');
  const objectives = (Array.isArray(d.objectives) ? d.objectives : []).map((o) => ({
    _key: `o-${o.id}-${Math.random().toString(36).slice(2)}`,
    sortOrder: Number(o.sortOrder ?? 0),
    objectiveText: o.objectiveText ?? '',
    successIndicator: o.successIndicator ?? '',
    responsibleDepartmentId: o.responsibleDepartmentId != null ? String(o.responsibleDepartmentId) : '',
  }));

  const kpis = (Array.isArray(d.kpis) ? d.kpis : []).map((k) => {
    const relSort = k.relatedObjectiveSortOrder;
    let relIdx = '';
    if (relSort != null && relSort !== '') {
      const ix = objectives.findIndex((o) => Number(o.sortOrder) === Number(relSort));
      relIdx = ix >= 0 ? String(ix) : '';
    }
    return {
      _key: `k-${k.id}-${Math.random().toString(36).slice(2)}`,
      sortOrder: Number(k.sortOrder ?? 0),
      relatedObjectiveSortOrder: relIdx,
      name: k.name ?? '',
      targetValue: k.targetValue ?? '',
      responsibleDepartmentId: k.responsibleDepartmentId != null ? String(k.responsibleDepartmentId) : '',
      quarter1: k.quarter1 ?? '',
      quarter2: k.quarter2 ?? '',
      quarter3: k.quarter3 ?? '',
      measurementMethod: k.measurementMethod ?? '',
    };
  });

  return {
    branchId: d.companyId != null ? String(d.companyId) : '',
    departmentId: d.departmentId != null ? String(d.departmentId) : '',
    title: d.title ?? '',
    status: d.status ?? 'draft',
    periodStart: toDateInput(d.periodStartUtc),
    periodEnd: toDateInput(d.periodEndUtc),
    preparedBy: d.preparedBy ?? '',
    approvedBy: d.approvedBy ?? '',
    overviewDescription: d.overviewDescription ?? '',
    overviewPurpose: d.overviewPurpose ?? '',
    vision: d.vision ?? '',
    mission: d.mission ?? '',
    valuesText: d.valuesText ?? '',
    reviewedBy: d.reviewedBy ?? '',
    approvalConfirmed: !!d.approvalConfirmed,
    objectives,
    axes: (Array.isArray(d.axes) ? d.axes : []).map((x) => ({
      _key: `x-${x.id}-${Math.random().toString(36).slice(2)}`,
      sortOrder: Number(x.sortOrder ?? 0),
      name: x.name ?? '',
      departmentId: x.departmentId != null ? String(x.departmentId) : '',
      activities: (Array.isArray(x.activities) ? x.activities : []).map((a) => ({
        _key: `a-${a.id}-${Math.random().toString(36).slice(2)}`,
        sortOrder: Number(a.sortOrder ?? 0),
        name: a.name ?? '',
        description: a.description ?? '',
        startDate: toDateInput(a.startDateUtc),
        endDate: toDateInput(a.endDateUtc),
        responsiblePersonName: a.responsiblePersonName ?? '',
        responsibleDepartmentId: a.responsibleDepartmentId != null ? String(a.responsibleDepartmentId) : '',
        status: a.status ?? 'planned',
        progressPercentage: Number(a.progressPercentage ?? 0),
      })),
    })),
    hrResources: (Array.isArray(d.hrResources) ? d.hrResources : []).map((h) => ({
      _key: `h-${h.id}-${Math.random().toString(36).slice(2)}`,
      sortOrder: Number(h.sortOrder ?? 0),
      role: h.role ?? '',
      numberRequired: h.numberRequired != null ? String(h.numberRequired) : '',
      natureOfWork: h.natureOfWork ?? '',
      notes: h.notes ?? '',
    })),
    financialResources: (Array.isArray(d.financialResources) ? d.financialResources : []).map((f) => ({
      _key: `f-${f.id}-${Math.random().toString(36).slice(2)}`,
      sortOrder: Number(f.sortOrder ?? 0),
      budgetItem: f.budgetItem ?? '',
      estimatedAmount: f.estimatedAmount != null ? String(f.estimatedAmount) : '',
      actualAmount: f.actualAmount != null ? String(f.actualAmount) : '',
      notes: f.notes ?? '',
    })),
    kpis,
    risks: (Array.isArray(d.risks) ? d.risks : []).map((r) => ({
      _key: `r-${r.id}-${Math.random().toString(36).slice(2)}`,
      sortOrder: Number(r.sortOrder ?? 0),
      description: r.description ?? '',
      probability: r.probability ?? '',
      impact: r.impact ?? '',
      mitigationPlan: r.mitigationPlan ?? '',
    })),
  };
}

function toUtcNoonDate(isoDate) {
  if (!isoDate || !String(isoDate).trim()) return null;
  const d = String(isoDate).trim();
  return `${d}T12:00:00Z`;
}

function numOrNull(s) {
  const t = String(s ?? '').trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function buildUpsertPayload(form) {
  const objectives = (form.objectives || [])
    .filter((o) => (o.objectiveText || '').trim())
    .map((o, i) => ({
      sortOrder: i,
      objectiveText: o.objectiveText.trim(),
      successIndicator: (o.successIndicator || '').trim() || null,
      responsibleDepartmentId: numOrNull(o.responsibleDepartmentId),
    }));

  const axes = (form.axes || [])
    .filter((x) => (x.name || '').trim())
    .map((x, xi) => ({
      sortOrder: xi,
      name: x.name.trim(),
      departmentId: numOrNull(x.departmentId),
      activities: (x.activities || [])
        .filter((a) => (a.name || '').trim())
        .map((a, ai) => ({
          sortOrder: ai,
          name: a.name.trim(),
          description: (a.description || '').trim() || null,
          startDateUtc: toUtcNoonDate(a.startDate),
          endDateUtc: toUtcNoonDate(a.endDate),
          responsiblePersonName: (a.responsiblePersonName || '').trim() || null,
          responsibleDepartmentId: numOrNull(a.responsibleDepartmentId),
          status: a.status || 'planned',
          progressPercentage: Math.min(100, Math.max(0, Number(a.progressPercentage) || 0)),
        })),
    }));

  const hrResources = (form.hrResources || [])
    .filter((h) => (h.role || '').trim())
    .map((h, i) => ({
      sortOrder: i,
      role: h.role.trim(),
      numberRequired: numOrNull(h.numberRequired),
      natureOfWork: (h.natureOfWork || '').trim() || null,
      notes: (h.notes || '').trim() || null,
    }));

  const financialResources = (form.financialResources || [])
    .filter((f) => (f.budgetItem || '').trim())
    .map((f, i) => ({
      sortOrder: i,
      budgetItem: f.budgetItem.trim(),
      estimatedAmount: numOrNull(f.estimatedAmount),
      actualAmount: numOrNull(f.actualAmount),
      notes: (f.notes || '').trim() || null,
    }));

  const kpis = (form.kpis || [])
    .filter((k) => (k.name || '').trim())
    .map((k, i) => {
      const idx = numOrNull(k.relatedObjectiveSortOrder);
      const rel = idx != null && idx >= 0 && idx < objectives.length ? idx : null;
      return {
        sortOrder: i,
        relatedObjectiveSortOrder: rel,
        name: k.name.trim(),
        targetValue: (k.targetValue || '').trim() || null,
        responsibleDepartmentId: numOrNull(k.responsibleDepartmentId),
        quarter1: (k.quarter1 || '').trim() || null,
        quarter2: (k.quarter2 || '').trim() || null,
        quarter3: (k.quarter3 || '').trim() || null,
        measurementMethod: (k.measurementMethod || '').trim() || null,
      };
    });

  const risks = (form.risks || [])
    .filter((r) => (r.description || '').trim())
    .map((r, i) => ({
      sortOrder: i,
      description: r.description.trim(),
      probability: (r.probability || '').trim() || null,
      impact: (r.impact || '').trim() || null,
      mitigationPlan: (r.mitigationPlan || '').trim() || null,
    }));

  return {
    companyId: Number(form.branchId),
    departmentId: numOrNull(form.departmentId),
    title: form.title.trim(),
    status: form.status || 'draft',
    periodStartUtc: toUtcNoonDate(form.periodStart),
    periodEndUtc: toUtcNoonDate(form.periodEnd),
    preparedBy: (form.preparedBy || '').trim() || null,
    approvedBy: (form.approvedBy || '').trim() || null,
    overviewDescription: (form.overviewDescription || '').trim() || null,
    overviewPurpose: (form.overviewPurpose || '').trim() || null,
    vision: (form.vision || '').trim() || null,
    mission: (form.mission || '').trim() || null,
    valuesText: (form.valuesText || '').trim() || null,
    reviewedBy: (form.reviewedBy || '').trim() || null,
    approvalConfirmed: !!form.approvalConfirmed,
    objectives,
    axes,
    hrResources,
    financialResources,
    kpis,
    risks,
  };
}

export async function fetchOperationalPlans() {
  const res = await fetch(`${API_BASE}/api/operational-plans`, {
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر تحميل الخطط التشغيلية'));
  return Array.isArray(data) ? data : [];
}

export async function fetchOperationalPlanById(id) {
  const res = await fetch(`${API_BASE}/api/operational-plans/${id}`, {
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر تحميل الخطة'));
  return data;
}

export async function createOperationalPlan(payload) {
  const res = await fetch(`${API_BASE}/api/operational-plans`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر حفظ الخطة'));
  return data;
}

export async function updateOperationalPlan(id, payload) {
  const res = await fetch(`${API_BASE}/api/operational-plans/${id}/edit`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر حفظ التعديلات'));
  return data;
}

export async function deleteOperationalPlan(id) {
  const res = await fetch(`${API_BASE}/api/operational-plans/${id}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...authHeaders() },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(messageFromErrorBody(data, 'تعذر حذف الخطة'));
  }
}
